import {
  AliasType,
  ContactType,
  LicensePaginationSort,
  PaginationSort,
  Provider,
  GetBrokerageQuery,
} from '../gql/graphql';
import {
  getFirstPaginatedNode,
  getPaginatedNodes,
  toPagination,
} from './utils';

import { convergeLogger } from '@converge/config';
import { graphql } from '../gql';
import moment from 'moment';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_BROKERAGE = graphql(`
  query getBrokerage(
    $pagination: PaginationRequest
    $filters: EntityFilterInput!
    $sort: PaginationSort
    $licenseSort: LicensePaginationSort
  ) {
    entities(pagination: $pagination, filters: $filters, sort: $sort) {
      edges {
        cursor
        node {
          uuid
          referenceId
          type
          status
          fein
          npn
          agency
          brokerCommission
          wholesaleCommission
          digitalCommission
          retailCommission
          insurer
          createdAt
          contacts {
            edges {
              cursor
              node {
                uuid
                type
                value
                source
                primary
              }
            }
          }

          uwTerritories {
            edges {
              node {
                uuid
                name
              }
            }
          }
          bdmTerritories {
            edges {
              node {
                uuid
                name
              }
            }
          }

          aliases(filters: { source: PRIMARY }) {
            edges {
              cursor
              queryCursor
              node {
                uuid
                name
                type
                source
                primary
              }
            }
          }
          program {
            uuid
            name
          }
          offices(
            pagination: { after: 0, first: 1000 }
            sort: { key: "created_at", order: "DESC" }
          ) {
            edges {
              node {
                uuid
                createdAt
                users {
                  edges {
                    cursor
                    node {
                      uuid
                      username
                      userId
                      groups
                      type
                    }
                  }
                }
                persons {
                  edges {
                    cursor
                    node {
                      personType {
                        primary
                      }
                      uuid
                      primary
                      primaryContact
                      firstName
                      lastName
                      users {
                        edges {
                          cursor
                          node {
                            uuid
                            username
                            userId
                            groups
                            type
                          }
                        }
                      }

                      contacts {
                        edges {
                          cursor
                          node {
                            uuid
                            type
                            value
                            source
                            primary
                          }
                        }
                      }
                    }
                  }
                }
                address {
                  uuid
                  street1
                  street2
                  city
                  type
                  postalCode
                  source
                  regions {
                    edges {
                      cursor
                      node {
                        uuid
                        name
                        iso2Code
                      }
                    }
                  }

                  countries {
                    edges {
                      cursor
                      node {
                        uuid
                        name
                        iso2Code
                      }
                    }
                  }
                  primary
                }
                useCorpAddress
                name
                uwTerritories {
                  edges {
                    node {
                      uuid
                      name
                    }
                  }
                }
                bdmTerritories {
                  edges {
                    node {
                      uuid
                      name
                    }
                  }
                }
              }
            }
          }
          teams(
            pagination: { after: 0, first: 1000 }
            sort: { key: "created_at", order: "DESC" }
          ) {
            edges {
              node {
                uuid
                name
                uwTerritories {
                  edges {
                    node {
                      uuid
                      name
                    }
                  }
                }
                bdmTerritories {
                  edges {
                    node {
                      uuid
                      name
                    }
                  }
                }

                persons {
                  edges {
                    cursor
                    node {
                      personType {
                        primary
                      }
                      uuid
                      primary
                      firstName
                      lastName
                      primaryContact
                      teamLead
                      contacts {
                        edges {
                          cursor
                          node {
                            uuid
                            type
                            value
                            source
                            primary
                          }
                        }
                      }
                    }
                  }
                }
                users {
                  edges {
                    cursor
                    node {
                      uuid
                      username
                      userId
                      groups
                      type
                    }
                  }
                }
              }
            }
          }

          parents(filters: { depthSearch: 1 }) {
            edges {
              node {
                uuid
                depthLevel
                referenceId
                aliases {
                  edges {
                    cursor
                    queryCursor
                    node {
                      uuid
                      name
                      type
                      source
                      primary
                    }
                  }
                }
              }
            }
          }
          partner {
            edges {
              node {
                uuid
                referenceId
                aliases {
                  edges {
                    cursor
                    queryCursor
                    node {
                      uuid
                      name
                      type
                      source
                      primary
                    }
                  }
                }
              }
            }
          }
          persons(
            pagination: { after: 0, first: 1000 }
            filters: { active: true, status: ACTIVE }
          ) {
            edges {
              cursor
              node {
                uuid
                agent
                firstName
                lastName
                primaryContact
                source
                primary
                contacts {
                  edges {
                    cursor
                    node {
                      uuid
                      type
                      value
                      source
                      primary
                    }
                  }
                }
              }
            }
          }
          licenses(pagination: { after: 0, first: 1000 }, sort: $licenseSort) {
            edges {
              cursor
              node {
                uuid
                perpetual
                licenseNumber
                expirationDate
                licenseType
                source
                forState
                region {
                  name
                  iso2Code
                  uuid
                }
              }
            }
          }
          users {
            edges {
              cursor
              node {
                uuid
                username
                userId
                groups
                type
              }
            }
          }

          domains {
            edges {
              cursor
              node {
                uuid
                domain
                sources
                primary
              }
            }
          }
          addresses(filters: { source: PRIMARY }) {
            edges {
              cursor
              node {
                uuid
                street1
                street2
                city
                type
                postalCode
                source
                regions {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                countries {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                primary
              }
            }
          }
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);

export function useGetBrokerage({
  pageNumber = 0,
  pageSize = 15,
  uuid,
  isParent = undefined,
  enabled,
  sort,
  licenseSort,
}: {
  pageNumber?: number;
  pageSize?: number;
  uuid?: string;
  isParent?: boolean;
  enabled?: boolean;
  sort?: PaginationSort;
  licenseSort?: LicensePaginationSort;
}) {
  const variables = {
    pagination: toPagination(pageNumber, pageSize),
    filters: {},
    sort,
    licenseSort,
  };
  if (isParent !== undefined) {
    variables.filters = {
      agency: true,
      entityUuid: uuid,
      parents: isParent,
    };
  } else {
    variables.filters = {
      agency: true,
      entityUuid: uuid,
    };
  }
  const { loading, error, data } = useQuery(GET_BROKERAGE, {
    variables,
    skip: !enabled,
    fetchPolicy: 'no-cache',
  }) as { loading: boolean; error: unknown; data?: GetBrokerageQuery };

  const brokerages = useMemo(() => {
    if (!data || !data.entities || !data.entities.edges) return [];

    return getPaginatedNodes(data.entities)
      .filter((e) => e.agency)
      .map((edge) => {
        const alias = getPaginatedNodes(edge?.aliases || { edges: [] });

        const parentNode = getFirstPaginatedNode(
          edge?.parents || { edges: [] }
        );
        const parentAlias = getPaginatedNodes(
          parentNode?.aliases || { edges: [] }
        ).find((alias) => alias.type === AliasType.LegalName && alias.primary);

        const partnerNode = getFirstPaginatedNode(
          edge?.partner || { edges: [] }
        );
        const partnerAlias = getPaginatedNodes(
          partnerNode?.aliases || { edges: [] }
        ).find((alias) => alias.type === AliasType.LegalName && alias.primary);
        if (!alias || alias.length === 0) {
          convergeLogger.error(
            `No primary legal name alias found for broker ${edge.uuid}`
          );
        }

        const primaryAddress = edge.addresses.edges.find((item) => {
          const node = item.node;
          return node?.type?.includes('CORPORATE');
        });

        const billingAddress = edge.addresses.edges.find((item) => {
          const node = item.node;
          return node?.type?.includes('BILLING');
        });

        const brokerageName = alias.find((alias) => {
          return (
            alias.type === AliasType.LegalName &&
            alias.source === Provider.Primary
          );
        });

        const brokerageTradingName = alias.find((alias) => {
          return (
            alias.type === AliasType.TradingName &&
            alias.source === Provider.Primary
          );
        });

        return {
          id: edge?.uuid,
          brokerageName: brokerageName?.name ?? null,
          brokerageNameUuid: brokerageName?.uuid ?? null,
          dbaName: brokerageTradingName?.name ?? null,
          dbaUuid: brokerageTradingName?.uuid ?? null,
          uwRegion: edge?.uwTerritories?.edges[0]?.node?.name ?? '',
          domainName:
            getPaginatedNodes(edge?.domains || { edges: [] })[0]?.domain ?? '',
          parentUuid:
            getPaginatedNodes(edge?.parents || { edges: [] })[0]?.uuid ?? '',
          partnerUuid:
            getPaginatedNodes(edge?.partner || { edges: [] })[0]?.uuid ?? '',
          fein: edge?.fein ?? '',
          wholesaleCommission: edge?.wholesaleCommission
            ? edge?.wholesaleCommission * 100
            : null,
          digitalCommission: edge?.digitalCommission
            ? edge?.digitalCommission * 100
            : null,
          retailCommission: edge?.retailCommission
            ? edge?.retailCommission * 100
            : null,
          primaryAddressUuid: primaryAddress?.node.uuid,
          primaryStreet1: primaryAddress?.node.street1 ?? '',
          primaryStreet2: primaryAddress?.node.street2 ?? '',
          primaryCity: primaryAddress?.node.city ?? '',
          primaryState:
            primaryAddress?.node?.regions?.edges[0]?.node?.name ?? '',
          primaryStateUuid:
            primaryAddress?.node?.regions?.edges[0]?.node?.uuid ?? '',
          primaryPostalCode: primaryAddress?.node?.postalCode ?? '',
          primaryCountry:
            primaryAddress?.node?.countries?.edges[0]?.node.uuid ?? '',
          billingAddressUuid: billingAddress?.node.uuid,
          billingStreet1: billingAddress?.node.street1 ?? '',
          billingStreet2: billingAddress?.node.street2 ?? '',
          billingCity: billingAddress?.node.city ?? '',
          billingState:
            billingAddress?.node?.regions?.edges[0]?.node?.name ?? '',
          billingStateUuid:
            billingAddress?.node?.regions?.edges[0]?.node?.uuid ?? '',
          billingPostalCode: billingAddress?.node?.postalCode ?? '',
          billingCountry:
            primaryAddress?.node?.countries?.edges[0]?.node.uuid ?? '',
          convergeRegion:
            edge?.offices?.edges[0]?.node?.uwTerritories?.edges[0]?.node
              ?.name ?? '',
          brokers: edge?.persons?.edges?.length,
          onboarded: moment(edge?.createdAt?.split('T')[0]).format(
            'MM/DD/YYYY'
          ),
          parentBrokerage: parentAlias?.name,
          partnerName: partnerAlias?.name,
          licenses: edge.licenses.edges,
          person: edge.persons,
          user: edge?.users,
          uWRegion: edge?.uwTerritories?.edges[0]?.node?.uuid,
          BdmRegion: edge?.bdmTerritories?.edges[0]?.node?.uuid,
          teams: edge.teams.edges,
          listOfBrokers: edge?.persons?.edges,
          offices: edge.offices.edges,
          programUuid: edge.program?.uuid,
          status: edge.status,
          primaryPhoneNo:
            edge.contacts.edges.filter(
              (item) => item.node.type === ContactType.Phone
            )[0]?.node?.value ?? '',
          primaryPhoneUuid: edge.contacts.edges.filter(
            (item) => item.node.type === ContactType.Phone
          )[0]?.node?.uuid,
        };
      });
  }, [data]);

  return {
    error,
    loading,
    data,
    brokerages,
  };
}
